import React from 'react';
import { Settings, MonitorPlay, ClipboardList, Briefcase } from 'lucide-react';
import { motion } from 'framer-motion';

const SkillToDeployPage = () => {
    return (
        <div className="bg-brand-bg-50 min-h-[85vh] pb-12 lg:pb-24 relative overflow-hidden font-poppins">
            {/* Background Decorations */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-30 pointer-events-none">
                <motion.div
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.5, 0.3],
                        rotate: [0, 45, 0],
                    }}
                    transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-0 left-1/4 w-96 h-96 bg-blue-200/50 rounded-full blur-3xl"
                />
                <motion.div
                    animate={{
                        scale: [1, 1.1, 1],
                        opacity: [0.3, 0.5, 0.3],
                        rotate: [0, -45, 0],
                    }}
                    transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                    className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-200/50 rounded-full blur-3xl"
                />
            </div>

            <div className="max-w-7xl mx-auto px-6 relative z-10 flex flex-col lg:flex-row items-center gap-12 lg:gap-8">
                {/* Left Side: Details */}
                <div className="w-full lg:w-1/2">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-8"
                    >
                        <span className="bg-brand-gold/10 text-brand-gold text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest border border-brand-gold/20 shadow-sm mb-6 inline-block">
                            Proven Career Pathway
                        </span>
                        <h1 className="text-3xl md:text-4xl font-semibold text-brand-textLight mb-6 leading-tight">
                            Transform Your <span className="md:whitespace-nowrap">Potential into</span> <span className="text-brand-gold md:whitespace-nowrap">Professional Success</span>
                        </h1>
                        <p className="text-lg text-brand-textMuted mb-10 leading-relaxed max-w-xl">
                            Our structured "Skill to Deploy" framework bridges the gap between learning and your dream career with guaranteed outcomes.
                        </p>
                    </motion.div>
                </div>

                {/* Right Side: Scaled down Animation */}
                <div className="w-full lg:w-1/2 flex lg:justify-end items-start mt-12 lg:mt-12 pt-0 self-start lg:-mr-12">
                    {/* Wrap the animation in a scaled container to make it smaller */}
                    <div className="relative w-full aspect-square max-w-[500px] flex items-center justify-center transform scale-75 md:scale-90 lg:scale-[0.9] origin-top right-0">
                        {/* Connecting Rings & Arcs */}
                        <svg className="absolute w-[800px] h-[600px] pointer-events-none z-0" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
                            <defs>
                                <linearGradient id="arc-gradient-1" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#22d3ee" stopOpacity="0" />
                                    <stop offset="50%" stopColor="#22d3ee" stopOpacity="1" />
                                    <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
                                </linearGradient>
                                <linearGradient id="arc-gradient-2" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#60a5fa" stopOpacity="0" />
                                    <stop offset="50%" stopColor="#60a5fa" stopOpacity="1" />
                                    <stop offset="100%" stopColor="#60a5fa" stopOpacity="0" />
                                </linearGradient>
                            </defs>

                            {/* Dashed Orbital Path */}
                            <motion.circle
                                initial={{ pathLength: 0, opacity: 0 }}
                                animate={{ pathLength: 1, opacity: 0.6 }}
                                transition={{ duration: 2, ease: "easeInOut" }}
                                cx="400" cy="300" r="180" fill="none" stroke="#e2e8f0" strokeWidth="2" strokeDasharray="6 6" className="opacity-60"
                            />

                            {/* Top Right Arc */}
                            <motion.path
                                d="M 400 120 A 180 180 0 0 1 580 300"
                                fill="none"
                                stroke="url(#arc-gradient-1)"
                                strokeWidth="5"
                                strokeLinecap="round"
                                className="drop-shadow-xl"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 1.5, delay: 0.5, ease: "easeInOut" }}
                            />

                            {/* Bottom Left Arc */}
                            <motion.path
                                d="M 400 480 A 180 180 0 0 1 220 300"
                                fill="none"
                                stroke="url(#arc-gradient-2)"
                                strokeWidth="5"
                                strokeLinecap="round"
                                className="drop-shadow-xl"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 1.5, delay: 0.5, ease: "easeInOut" }}
                            />

                            {/* Decorative dots on arcs */}
                            <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 2, duration: 0.3 }}>
                                <motion.circle
                                    cx="580" cy="300" r="8" fill="#22d3ee"
                                    animate={{ scale: [1, 1.5, 1], opacity: [1, 0.8, 1] }}
                                    transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 1 }}
                                />
                            </motion.g>
                            <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 2, duration: 0.3 }}>
                                <motion.circle
                                    cx="220" cy="300" r="8" fill="#60a5fa"
                                    animate={{ scale: [1, 1.5, 1], opacity: [1, 0.8, 1] }}
                                    transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 1, delay: 0.5 }}
                                />
                            </motion.g>
                        </svg>

                        {/* Orbiting Particles */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                            <motion.div
                                className="absolute w-[360px] h-[360px] rounded-full"
                                animate={{ rotate: 360 }}
                                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                            >
                                <div className="absolute top-0 right-1/2 w-3 h-3 bg-blue-400 rounded-full blur-[2px]"></div>
                            </motion.div>
                            <motion.div
                                className="absolute w-[360px] h-[360px] rounded-full"
                                animate={{ rotate: -360 }}
                                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                            >
                                <div className="absolute bottom-0 left-1/2 w-2 h-2 bg-purple-400 rounded-full blur-[1px]"></div>
                            </motion.div>
                        </div>

                        {/* Central Circle */}
                        <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: "spring", stiffness: 260, damping: 20, duration: 0.8 }}
                            className="relative z-20 w-64 h-64 rounded-full bg-brand-card shadow-[0_0_80px_-10px_rgba(37,99,235,0.4)] flex flex-col items-center justify-center border-4 border-gray-800"
                        >
                            <div className="absolute inset-0 rounded-full border-2 border-blue-50/50 animate-[spin_8s_linear_infinite]"></div>
                            <div className="absolute inset-2 rounded-full border border-dashed border-blue-200 animate-[spin_15s_linear_infinite_reverse]"></div>

                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5, duration: 0.5 }}
                                className="text-center z-10"
                            >
                                <h2 className="text-3xl md:text-4xl font-semibold text-brand-textLight leading-tight">
                                    Skill to<br />
                                    <span className="text-brand-gold">Deploy</span>
                                </h2>
                            </motion.div>

                            {/* Subtle inner pulse */}
                            <div className="absolute inset-4 rounded-full border border-blue-100/30 animate-ping opacity-20"></div>
                        </motion.div>

                        {/* Hovering Icons purely for the animation effect, placed around the ring without text */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 1, duration: 0.5 }}
                            className="hidden md:block absolute top-[8%] left-[12%] z-30"
                        >
                            <div className="w-14 h-14 bg-brand-card rounded-xl shadow-lg border border-gray-800 flex items-center justify-center animate-[bounce_3s_infinite]">
                                <Settings className="w-7 h-7 text-brand-gold" />
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 1.2, duration: 0.5 }}
                            className="hidden md:block absolute top-[18%] right-[8%] z-30"
                        >
                            <div className="w-14 h-14 bg-brand-card rounded-xl shadow-lg border border-gray-800 flex items-center justify-center animate-[bounce_4s_infinite]">
                                <MonitorPlay className="w-7 h-7 text-cyan-500" />
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 1.4, duration: 0.5 }}
                            className="hidden md:block absolute bottom-[8%] right-[8%] z-30"
                        >
                            <div className="w-14 h-14 bg-brand-card rounded-xl shadow-lg border border-gray-800 flex items-center justify-center animate-[bounce_3.5s_infinite]">
                                <ClipboardList className="w-7 h-7 text-cyan-600" />
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 1.6, duration: 0.5 }}
                            className="hidden md:block absolute bottom-[10%] left-[8%] z-30"
                        >
                            <div className="w-14 h-14 bg-brand-card rounded-xl shadow-lg border border-gray-800 flex items-center justify-center animate-[bounce_4.5s_infinite]">
                                <Briefcase className="w-7 h-7 text-indigo-600" />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Full Width Steps Section Below */}
            <div className="max-w-7xl mx-auto px-6 relative z-10 mt-8 lg:mt-12">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">
                    {[
                        {
                            id: 1,
                            title: "Intensive Skill Training",
                            subtitle: "6 Months + 150 Industry Projects",
                            description: "Master the most in-demand technologies through rigorous, hands-on training and build a robust portfolio with real-world industry projects.",
                            icon: <Settings className="w-6 h-6 text-white" />,
                            color: "bg-blue-600"
                        },
                        {
                            id: 2,
                            title: "Virtual Internship",
                            subtitle: "2 Months Real-world Experience",
                            description: "Bridge the gap between learning and doing by working in simulated corporate environments and gaining practical exposure to industry workflows.",
                            icon: <MonitorPlay className="w-6 h-6 text-white" />,
                            color: "bg-purple-600"
                        },
                        {
                            id: 3,
                            title: "3 Rigorous Tests",
                            subtitle: "Communication, Business Logic, Aptitude",
                            description: "Validate your career readiness through comprehensive evaluations designed to assess your technical aptitude, business intelligence, and communication skills.",
                            icon: <ClipboardList className="w-6 h-6 text-white" />,
                            color: "bg-cyan-600"
                        },
                        {
                            id: 4,
                            title: "Unlimited Opportunities",
                            subtitle: "Until You Get Placed",
                            description: "Receive dedicated, end-to-end placement support including access to our extensive hiring network and interview preparation until you land your dream job.",
                            icon: <Briefcase className="w-6 h-6 text-white" />,
                            color: "bg-indigo-600"
                        },
                    ].map((step, idx) => (
                        <motion.div
                            key={step.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * idx }}
                            whileHover={{ scale: 1.02 }}
                            className="bg-brand-card p-6 md:p-8 rounded-2xl shadow-sm border border-gray-800 flex items-start sm:items-center gap-6 cursor-pointer hover:border-brand-gold/50 hover:shadow-[0_0_15px_rgba(255,184,28,0.15)] transition-all"
                        >
                            <div className={`${step.color} w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 shadow-md`}>
                                {step.icon}
                            </div>
                            <div className="flex-1">
                                <h3 className="text-xl md:text-2xl font-semibold text-brand-textLight leading-tight mb-2">{step.title}</h3>
                                <p className="text-brand-gold font-bold text-sm md:text-base mb-3">{step.subtitle}</p>
                                <p className="text-brand-textMuted text-sm md:text-base leading-relaxed">{step.description}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>

    );
};

export default SkillToDeployPage;
